


import { createSlice } from "@reduxjs/toolkit";
import {
  fetchDoctorSlotsForDay,
  fetchAvailableSlotsForDay,
  createAvailabilityRange,
  updateAvailabilitySlot,
  deleteAvailabilitySlot,
  markRangeUnavailable,
  markRangeAvailable,
  markSlotsUnavailable,
  markSlotsAvailable,
} from "../thunks/availabilityThunks";

const initialState = {
  slots: [],
  availableSlots: [],
  lastMedecinId: null,
  lastDate: null,
  status: "idle",
  error: null,
  updating: false,
};

// 🔧 Helper pour être TOLÉRANT au format renvoyé par l’API
// - soit [ ...slots ]
// - soit { slots: [ ... ] }
const normalizeSlots = (slotsFromPayload) => {
  if (Array.isArray(slotsFromPayload)) return slotsFromPayload;
  if (slotsFromPayload && Array.isArray(slotsFromPayload.slots)) {
    return slotsFromPayload.slots;
  }
  return [];
};

const availabilitySlice = createSlice({
  name: "availability",
  initialState,
  reducers: {
    resetAvailabilityState: () => initialState,
  },
  extraReducers: (builder) => {
    /* ===========================
     *       FETCH ALL SLOTS
     * =========================== */
    builder
      .addCase(fetchDoctorSlotsForDay.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchDoctorSlotsForDay.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.error = null;
        state.lastMedecinId = action.payload.medecinId;
        state.lastDate = action.payload.date;

        const normalized = normalizeSlots(action.payload.slots);
        state.slots = normalized;
        state.availableSlots = normalized.filter((s) => s.isAvailable);
      })
      .addCase(fetchDoctorSlotsForDay.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error.message;
      });

    /* ===========================
     *   FETCH AVAILABLE SLOTS
     * =========================== */
    builder
      .addCase(fetchAvailableSlotsForDay.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchAvailableSlotsForDay.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.error = null;
        state.lastMedecinId = action.payload.medecinId;
        state.lastDate = action.payload.date;

        const normalized = normalizeSlots(action.payload.slots);
        state.availableSlots = normalized;
      })
      .addCase(fetchAvailableSlotsForDay.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error.message;
      });

    /* ===========================
     *   CREATE RANGE → SLOTS
     * =========================== */
    builder
      .addCase(createAvailabilityRange.pending, (state) => {
        state.updating = true;
        state.error = null;
      })
      .addCase(createAvailabilityRange.fulfilled, (state, action) => {
        state.updating = false;

        const created = normalizeSlots(action.payload);
        state.slots = [...state.slots, ...created];
        state.availableSlots = state.slots.filter((s) => s.isAvailable);
      })
      .addCase(createAvailabilityRange.rejected, (state, action) => {
        state.updating = false;
        state.error = action.payload || action.error.message;
      });

    /* ===========================
     *          UPDATE
     * =========================== */
    builder
      .addCase(updateAvailabilitySlot.pending, (state) => {
        state.updating = true;
        state.error = null;
      })
      .addCase(updateAvailabilitySlot.fulfilled, (state, action) => {
        state.updating = false;
        const updated = action.payload;
        if (!updated || !updated.id) return;

        state.slots = state.slots.map((slot) =>
          slot.id === updated.id ? updated : slot
        );
        state.availableSlots = state.slots.filter((s) => s.isAvailable);
      })
      .addCase(updateAvailabilitySlot.rejected, (state, action) => {
        state.updating = false;
        state.error = action.payload || action.error.message;
      });

    /* ===========================
     *          DELETE
     * =========================== */
    builder
      .addCase(deleteAvailabilitySlot.pending, (state) => {
        state.updating = true;
        state.error = null;
      })
      .addCase(deleteAvailabilitySlot.fulfilled, (state, action) => {
        state.updating = false;
        const deletedId = action.payload;
        state.slots = state.slots.filter((s) => s.id !== deletedId);
        state.availableSlots = state.availableSlots.filter(
          (s) => s.id !== deletedId
        );
      })
      .addCase(deleteAvailabilitySlot.rejected, (state, action) => {
        state.updating = false;
        state.error = action.payload || action.error.message;
      });

    /* ===========================
     *    MARK RANGE / SLOTS
     * =========================== */
    const markPending = (state) => {
      state.updating = true;
      state.error = null;
    };
    const markFulfilled = (state) => {
      state.updating = false;
    };
    const markRejected = (state, action) => {
      state.updating = false;
      state.error = action.payload || action.error.message;
    };

    builder
      .addCase(markRangeUnavailable.pending, markPending)
      .addCase(markRangeUnavailable.fulfilled, markFulfilled)
      .addCase(markRangeUnavailable.rejected, markRejected);

    builder
      .addCase(markRangeAvailable.pending, markPending)
      .addCase(markRangeAvailable.fulfilled, markFulfilled)
      .addCase(markRangeAvailable.rejected, markRejected);

    builder
      .addCase(markSlotsUnavailable.pending, markPending)
      .addCase(markSlotsUnavailable.fulfilled, markFulfilled)
      .addCase(markSlotsUnavailable.rejected, markRejected);

    builder
      .addCase(markSlotsAvailable.pending, markPending)
      .addCase(markSlotsAvailable.fulfilled, markFulfilled)
      .addCase(markSlotsAvailable.rejected, markRejected);
  },
});

export const { resetAvailabilityState } = availabilitySlice.actions;
export default availabilitySlice.reducer;
